import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Slider,
  Stack,
  styled,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { classes } from '../../Components/Modals/styles';
import {
  ApplicationAction,
  ApplicationState,
  IApplication,
  IAttachment,
  IVideoInterview
} from '../types';
import { AttachmentType } from '../config';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Api from '../API';
import ExternalViewApi from '../../ExternalView/Api';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import TimestampTable from './Components/TimestampTable';
import ImagePreview from '../OverviewTab/Attachments/ImagePreview';
import VideoPreview from '../OverviewTab/Attachments/VideoPreview';
import AudioPreview from '../OverviewTab/Attachments/AudioPreview';
import TextPreview from '../OverviewTab/Attachments/TextPreview';
import PdfPreview from '../OverviewTab/Attachments/PdfPreview';
import WordPreview from '../OverviewTab/Attachments/WordPreview';
import { getAttachmentType } from '../../utils/attachment-type';
import { VIDEO_INTERVIEW_DISPLAY_NAME, VIDEO_INTERVIEW_SOURCE_INDEX } from '../config';
import { IUserPermissions } from '../../Components/sharedTypes';
import { ZoomInSVG, ZoomOutSVG } from '../OverviewTab/Icons';
import { theme } from '../../../../components/ThemeContext/ThemeObject';
import { useGesture } from '@use-gesture/react';
import { MAX_SCALE, ZOOM_STEP } from '../constants';
import { useThrottle } from '../../hooks';
import { ExternalViewAction } from '../../ExternalView/reducer';

dayjs.extend(duration);

const CustomSlider = styled(Slider)(() => ({
  ...classes.zoomSlider
}));

export default function AttachmentPreviewModal({
  previewOpen,
  dispatch,
  externalViewDispatch,
  selectedAttachment,
  ApplicationState,
  permissions
}: {
  previewOpen: boolean;
  dispatch?: React.Dispatch<ApplicationAction>;
  externalViewDispatch?: React.Dispatch<ExternalViewAction>;
  selectedAttachment: IAttachment | IVideoInterview;
  ApplicationState?: ApplicationState;
  permissions: IUserPermissions | undefined;
}) {
  const BASE_URL = window.location.origin;
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(true);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const queryClient = useQueryClient();
  const application =
    queryClient.getQueryData<IApplication>(['application']) ||
    queryClient.getQueryData<any>(['externalViewData'])?.application;
  const isVideoInterview = 'watch_interview_link' in selectedAttachment;
  const attachmentType = getAttachmentType(selectedAttachment as IAttachment);
  const isVideoType = attachmentType === AttachmentType.Video || isVideoInterview;
  const [zoom, setZoom] = useState<number>(0);
  const [initialPdfZoomFactor, setInitialPdfZoomFactor] = React.useState<number | null>(null);

  const getZoomFactor = () => {
    if (attachmentType === AttachmentType.Pdf && initialPdfZoomFactor !== null) {
      const baseZoom = initialPdfZoomFactor < 1 ? initialPdfZoomFactor : 1;
      if (zoom === 0) {
        return baseZoom;
      }
      const zoomRange = 2 - baseZoom;
      return baseZoom + (zoom / MAX_SCALE) * zoomRange;
    }
    return 1 + zoom / MAX_SCALE;
  };

  const zoomFactor = getZoomFactor();
  const contentContainerRef = React.useRef<HTMLDivElement>(null);
  const [initialZoomCalculated, setInitialZoomCalculated] = React.useState<boolean>(false);
  const [isPdfZoomReady, setIsPdfZoomReady] = React.useState<boolean>(false);

  const calculatePdfZoomOnMount = React.useCallback(() => {
    const element = contentContainerRef.current;
    if (element && attachmentType === AttachmentType.Pdf && !initialZoomCalculated && previewOpen) {
      const containerWidth = element.clientWidth;
      if (containerWidth > 0) {
        const containerStyles = window.getComputedStyle(element);
        const paddingLeft = parseFloat(containerStyles.paddingLeft) || 0;
        const paddingRight = parseFloat(containerStyles.paddingRight) || 0;
        const availableWidth = containerWidth - paddingLeft - paddingRight;
        const pdfWidth = 750;
        const targetZoomFactor = availableWidth / pdfWidth;

        if (targetZoomFactor < 1) {
          setInitialPdfZoomFactor(targetZoomFactor);
          setZoom(0);
        }
        setInitialZoomCalculated(true);
        setIsPdfZoomReady(true);
      } else {
        setTimeout(() => calculatePdfZoomOnMount(), 50);
      }
    }
  }, [attachmentType, initialZoomCalculated, previewOpen]);

  const modalTitle = isVideoInterview
    ? VIDEO_INTERVIEW_DISPLAY_NAME
    : selectedAttachment.attached_file_name;

  const handleClose = () => {
    if (externalViewDispatch) {
      externalViewDispatch({ type: 'SET_PREVIEW_ATTACHMENT', payload: false });
      externalViewDispatch({ type: 'SET_SELECTED_ATTACHMENT', payload: null });
    }
    if (dispatch) {
      dispatch({
        type: 'SET_SELECTED_ATTACHMENT_ACTION',
        payload: { delete: null, preview: null }
      });
      dispatch({ type: 'RESET_TIMESTAMP_STATE' });
    }
    setIsLoadingPreview(true);
  };

  const throttledUpdateZoom = useThrottle((delta: number) => {
    setZoom((prevZoom) => {
      const zoomChange = Math.max(2, Math.round(2 * Math.abs(delta * 0.05)));
      const newZoom = delta > 0 ? prevZoom + zoomChange : prevZoom - zoomChange;

      return Math.min(MAX_SCALE, Math.max(0, newZoom));
    });
  }, 500);

  const onPinchHandler = useGesture({
    onPinch: ({ direction: [pinchDirection] }) => {
      const zoomDirection = pinchDirection < 0 ? -1 : 1;

      throttledUpdateZoom(zoomDirection);
    }
  });

  const externalViewData = queryClient.getQueryData<any>(['externalViewData']);
  const isExternalView = !!externalViewData && !!externalViewDispatch;

  const { data: previewData } = useQuery({
    queryKey: ['preview_data', currentIndex],
    queryFn: async () => {
      if (selectedAttachment.id && application && !isVideoInterview) {
        if (isExternalView) {
          const { res } = await ExternalViewApi.previewAttachment(
            application.job.id,
            application.id,
            externalViewData.id,
            externalViewData.login_hash,
            selectedAttachment.id,
            currentIndex
          );
          return res;
        } else {
          const { res } = await Api.previewAttachment(
            application.job.id,
            application.id,
            selectedAttachment.id,
            currentIndex
          );
          return res;
        }
      }
    },
    onError: (error: { message: string }) => {
      const payload = {
        message: `There was an error getting preview data, ${error}`,
        state: 'error' as const
      };
      dispatch && dispatch({ type: 'SET_SNACKBAR', payload });
      externalViewDispatch && externalViewDispatch({ type: 'SET_SNACKBAR', payload });
    },
    onSettled: () => setIsLoadingPreview(false),
    enabled:
      !!selectedAttachment &&
      (attachmentType === AttachmentType.Video || attachmentType === AttachmentType.Word) &&
      !isVideoInterview
  });

  const hasNoPreview = (attachmentType: AttachmentType | undefined): boolean => {
    if (!attachmentType) return true;
    if (attachmentType === AttachmentType.Word) return !previewData?.data;
    return false;
  };

  const handleDisplayContent = () => {
    const shouldDisablePreview = !isLoadingPreview && hasNoPreview(attachmentType);

    if (shouldDisablePreview) {
      return (
        <Box sx={{ ...classes.noPreview, height: 'unset' }}>
          No preview available for this file type.
        </Box>
      );
    }

    const videoInterviewSrc = isVideoInterview
      ? selectedAttachment.sources[VIDEO_INTERVIEW_SOURCE_INDEX]?.url
      : previewData?.video;

    const assetUrl = isExternalView
      ? `${BASE_URL}/api/v4/jobs/${application.job.id}/applications/${application.id}/application_external_view/${externalViewData.id}/assets/${selectedAttachment.id}?login_hash=${externalViewData.login_hash}`
      : undefined;

    const sharedProps = {
      onPinchHandler,
      zoom: zoomFactor,
      selectedAttachment: selectedAttachment as IAttachment
    };

    const components: Record<Exclude<AttachmentType, AttachmentType.Video>, JSX.Element | null> = {
      [AttachmentType.Image]: (
        <ImagePreview
          {...sharedProps}
          isLoadingPreview={isLoadingPreview}
          setIsLoadingPreview={setIsLoadingPreview}
          assetUrl={assetUrl}
          onImageLoad={calculateInitialZoom}
        />
      ),
      [AttachmentType.Audio]: (
        <AudioPreview
          selectedAttachment={selectedAttachment as IAttachment}
          setIsLoadingPreview={setIsLoadingPreview}
        />
      ),
      [AttachmentType.Text]: (
        <TextPreview
          selectedAttachment={selectedAttachment as IAttachment}
          dispatch={dispatch}
          externalViewDispatch={externalViewDispatch}
          setIsLoadingPreview={setIsLoadingPreview}
        />
      ),
      [AttachmentType.Pdf]: (
        <PdfPreview
          onPinchHandler={onPinchHandler}
          selectedAttachment={selectedAttachment as IAttachment}
          setIsLoadingPreview={setIsLoadingPreview}
          zoom={zoomFactor}
          assetUrl={assetUrl}
          onDocumentLoad={calculateInitialZoom}
          isZoomReady={isPdfZoomReady}
        />
      ),
      [AttachmentType.Word]: previewData ? (
        <WordPreview
          onPinchHandler={onPinchHandler}
          zoom={zoomFactor}
          selectedAttachment={selectedAttachment as IAttachment}
          previewData={previewData}
          setCurrentIndex={setCurrentIndex}
          onImageLoad={calculateInitialZoom}
        />
      ) : null
    };
    if (!attachmentType) return null;

    if (attachmentType === AttachmentType.Video || isVideoInterview) {
      return (
        <VideoPreview
          selectedAttachment={selectedAttachment}
          videoRef={videoRef}
          source={videoInterviewSrc}
          isLoadingPreview={isLoadingPreview}
          setIsLoadingPreview={setIsLoadingPreview}
        />
      );
    }

    return components[attachmentType] ?? null;
  };

  const handleZoomDecrement = () => {
    setZoom((prevZoom) => Math.max(0, prevZoom - ZOOM_STEP));
  };

  const handleZoomIncrement = () => {
    setZoom((prevZoom) => Math.min(MAX_SCALE, prevZoom + ZOOM_STEP));
  };

  const showZoomSlider =
    (attachmentType === AttachmentType.Pdf ||
      (attachmentType === AttachmentType.Word && previewData && 'data' in previewData) ||
      attachmentType === AttachmentType.Image) &&
    !isLoadingPreview;

  const calculateInitialZoom = React.useCallback(
    (contentWidth: number) => {
      if (!contentContainerRef.current || !contentWidth || initialZoomCalculated) return;

      const containerElement = contentContainerRef.current;
      const containerWidth = containerElement.clientWidth;
      const containerStyles = window.getComputedStyle(containerElement);
      const paddingLeft = parseFloat(containerStyles.paddingLeft) || 0;
      const paddingRight = parseFloat(containerStyles.paddingRight) || 0;
      const availableWidth = containerWidth - paddingLeft - paddingRight;

      if (availableWidth <= 0 || contentWidth <= 0) return;

      const targetZoomFactor = availableWidth / contentWidth;

      if (attachmentType === AttachmentType.Pdf && targetZoomFactor < 1) {
        setInitialPdfZoomFactor(targetZoomFactor);
        setZoom(0);
      } else {
        setInitialPdfZoomFactor(null);
        const initialZoomPercent = Math.min(
          MAX_SCALE,
          Math.max(0, Math.round((targetZoomFactor - 1) * MAX_SCALE))
        );
        setZoom(initialZoomPercent);
      }

      setInitialZoomCalculated(true);
    },
    [initialZoomCalculated, attachmentType]
  );

  useEffect(() => {
    if (!previewOpen) {
      setZoom(0);
      setInitialPdfZoomFactor(null);
      setInitialZoomCalculated(false);
      setIsPdfZoomReady(false);
      return;
    }

    if (attachmentType === AttachmentType.Pdf && previewOpen) {
      const timer = setTimeout(() => {
        calculatePdfZoomOnMount();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [previewOpen, attachmentType, calculatePdfZoomOnMount]);

  useEffect(() => {
    const handleWheelEvent = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault();
      }
    };
    document.addEventListener('wheel', handleWheelEvent, { passive: false });

    return () => {
      document.removeEventListener('wheel', handleWheelEvent);
    };
  }, []);

  return (
    <Modal open={previewOpen} onClose={handleClose} aria-labelledby="file-preview-modal">
      <Stack
        sx={{
          ...classes.attachmentPreviewModal,
          height: 'auto',
          maxHeight: { xs: '85vh', sm: '80vh' },
          minHeight: { xs: '40vh', sm: '40vh' },
          width: { xs: '90vw', sm: '900px' },
          padding: { xs: '16px', sm: '60px' }
        }}
      >
        <CloseIcon
          id="close-attachment-preview-button"
          onClick={handleClose}
          sx={classes.closeIcon}
          className="close-button"
        />
        <Box
          id="file-preview-modal-title"
          sx={{
            ...classes.attachmentPreviewTitle,
            fontSize: { xs: '20px', sm: '30px' },
            wordBreak: 'break-word',
            marginBottom: 3
          }}
        >
          {modalTitle}
        </Box>
        <Box
          ref={contentContainerRef}
          sx={{
            ...classes.attachmentPreviewContent,
            flexGrow: 1,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-x pan-y',
            minHeight: 0,
            margin: 'unset'
          }}
        >
          {handleDisplayContent()}
          {isLoadingPreview && (
            <Box
              sx={{
                ...classes.previewLoader,
                ...classes.absoluteLoader,
                margin: 'unset',
                bottom: '0px'
              }}
            >
              <CircularProgress sx={{ color: '#084D6D' }} />
              Loading Preview
            </Box>
          )}
        </Box>
        {showZoomSlider && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              ...classes.zoomSliderContainer,
              marginTop: { xs: theme.spacing(1), sm: theme.spacing(1.5) }
            }}
          >
            <Typography
              id="zoom-slider"
              variant="subtitle1"
              component="div"
              sx={{
                marginRight: { xs: 0, sm: `${theme.spacing(0.5)}` },
                marginBottom: { xs: theme.spacing(0.5), sm: 0 },
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              {zoom}%
            </Typography>
            <CustomSlider
              value={zoom}
              min={0}
              max={MAX_SCALE}
              step={ZOOM_STEP}
              onChange={(_e, value) => {
                setZoom(value as number);
              }}
              aria-labelledby="zoom-slider"
              size="small"
              sx={{
                width: { xs: '100px', sm: '150px' },
                marginBottom: { xs: theme.spacing(1), sm: 0 }
              }}
            />
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: { xs: 'center', sm: 'flex-start' }
              }}
            >
              <Button onClick={handleZoomDecrement} sx={classes.zoomIcons}>
                <ZoomOutSVG />
              </Button>
              <Button onClick={handleZoomIncrement} sx={classes.zoomIcons}>
                <ZoomInSVG />
              </Button>
            </Stack>
          </Stack>
        )}
        {(permissions?.Applications?.['Download Attachments'] || isExternalView) &&
          !isVideoType &&
          !isLoadingPreview && (
            <Box
              sx={{
                ...classes.attachmentPreviewActions,
                marginTop: { xs: '20px', sm: '30px' }
              }}
              marginTop={'auto'}
            >
              <a
                id="download-file-on-preview-button"
                href={
                  isExternalView
                    ? `${BASE_URL}/api/v4/jobs/${application.job.id}/applications/${application.id}/application_external_view/${externalViewData.id}/assets/${selectedAttachment.id}?login_hash=${externalViewData.login_hash}`
                    : `${BASE_URL}/admin/assets/${selectedAttachment.id}`
                }
              >
                <Button
                  id="download-attachment-button"
                  sx={{ ...classes.downloadButton, width: { xs: '100px', sm: '120px' } }}
                >
                  Download
                </Button>
              </a>
            </Box>
          )}
        {isVideoType && !isLoadingPreview && dispatch && (
          <Box
            width={'100%'}
            sx={{
              paddingY: { xs: '10px', sm: '20px' },
              paddingX: { xs: '10px', sm: '0px' }
            }}
            marginTop={'auto'}
          >
            <TimestampTable
              selectedAttachment={selectedAttachment}
              dispatch={dispatch}
              videoRef={videoRef}
              ApplicationState={ApplicationState}
            />
          </Box>
        )}
      </Stack>
    </Modal>
  );
}
